import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { LoginHistoryItem } from "@/api/models/api-types";

interface SecurityStore {
  // Password Form States
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  showCurrentPassword: boolean;
  showNewPassword: boolean;
  showConfirmPassword: boolean;

  // Password Form Actions
  setCurrentPassword: (password: string) => void;
  setNewPassword: (password: string) => void;
  setConfirmPassword: (password: string) => void;
  toggleShowCurrentPassword: () => void;
  toggleShowNewPassword: () => void;
  toggleShowConfirmPassword: () => void;
  resetPasswordForm: () => void;

  // Login History Management States
  loginHistory: LoginHistoryItem[];
  isLoginHistoryLoading: boolean;
  loginHistoryTotal: number;

  // Login History Management Actions
  setLoginHistory: (history: LoginHistoryItem[]) => void;
  setLoginHistoryLoading: (loading: boolean) => void;
  setLoginHistoryTotal: (total: number) => void;

  // Reset
  reset: () => void;
}

const initialState = {
  // Password Form States
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
  showCurrentPassword: false,
  showNewPassword: false,
  showConfirmPassword: false,

  // Login History Management States
  loginHistory: [],
  isLoginHistoryLoading: false,
  loginHistoryTotal: 0,
};

export const useSecurityStore = create<SecurityStore>()(
  persist(
    (set) => ({
      ...initialState,

      // Password Form Actions
      setCurrentPassword: (password: string) =>
        set({ currentPassword: password }),

      setNewPassword: (password: string) => set({ newPassword: password }),

      setConfirmPassword: (password: string) =>
        set({ confirmPassword: password }),

      toggleShowCurrentPassword: () =>
        set((state) => ({ showCurrentPassword: !state.showCurrentPassword })),

      toggleShowNewPassword: () =>
        set((state) => ({ showNewPassword: !state.showNewPassword })),

      toggleShowConfirmPassword: () =>
        set((state) => ({ showConfirmPassword: !state.showConfirmPassword })),

      resetPasswordForm: () =>
        set({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
          showCurrentPassword: false,
          showNewPassword: false,
          showConfirmPassword: false,
        }),

      // Login History Management Actions
      setLoginHistory: (history: LoginHistoryItem[]) =>
        set({ loginHistory: history }),

      setLoginHistoryLoading: (loading: boolean) =>
        set({ isLoginHistoryLoading: loading }),

      setLoginHistoryTotal: (total: number) =>
        set({ loginHistoryTotal: total }),

      // Reset
      reset: () => set(initialState),
    }),
    {
      name: "security-storage",
      partialize: () => ({
        // Don't persist sensitive data - they should reset on page reload
      }),
    }
  )
);
